/**
 * Tone Indicator Component
 *
 * Story 2.4: Visual indicator for tone/stress analysis
 *
 * Shows current stress level and trigger reasons on flashcards.
 * Uses color-coded icon to indicate stress state.
 */

import { Tooltip } from './Tooltip';

/** Tone-analysis specific reason codes (distinct from gate ReasonCode) */
type ToneReasonCode =
  | 'COMPETITOR_MENTION'
  | 'OBJECTION_KEYWORD'
  | 'QUESTION_DENSITY'
  | 'EXTENDED_SILENCE'
  | 'TALK_RATIO_IMBALANCE'
  | 'NEGATIVE_TONE'
  | 'NEGATIVE_TEXT_SENTIMENT'
  | 'HIGH_STRESS'
  | 'RAPID_SPEECH';

/** Reason code display labels */
const REASON_LABELS: Record<ToneReasonCode, string> = {
  COMPETITOR_MENTION: 'Competitor',
  OBJECTION_KEYWORD: 'Objection',
  QUESTION_DENSITY: 'Questions',
  EXTENDED_SILENCE: 'Silence',
  TALK_RATIO_IMBALANCE: 'Talk Ratio',
  NEGATIVE_TONE: 'Neg. Tone',
  NEGATIVE_TEXT_SENTIMENT: 'Neg. Sentiment',
  HIGH_STRESS: 'High Stress',
  RAPID_SPEECH: 'Fast Speech',
};

export interface ToneIndicatorProps {
  /** Show full details or just icon */
  compact?: boolean;
  /** Custom class name */
  className?: string;
  /** Current stress score (0-1) */
  stressScore: number;
  /** Active trigger reason codes */
  triggerReasons: string[];
}

/** Stress icon based on level - extracted to module scope to avoid re-creating on each render */
function StressIcon({ stressScore }: { stressScore: number }) {
  if (stressScore >= 0.7) {
    // High stress - warning triangle
    return (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
        />
      </svg>
    );
  }
  if (stressScore >= 0.4) {
    // Medium stress - exclamation
    return (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    );
  }
  // Low stress - checkmark
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

/**
 * ToneIndicator - Visual stress/tone indicator
 */
export function ToneIndicator({ compact = false, className = '', stressScore, triggerReasons }: ToneIndicatorProps) {
  // Determine stress level for color
  const getStressColor = () => {
    if (stressScore >= 0.7) return 'text-red-400';
    if (stressScore >= 0.4) return 'text-yellow-400';
    return 'text-green-400';
  };

  const getStressBgColor = () => {
    if (stressScore >= 0.7) return 'bg-red-500/20';
    if (stressScore >= 0.4) return 'bg-yellow-500/20';
    return 'bg-green-500/20';
  };

  if (compact) {
    // Just show icon with tooltip
    return (
      <Tooltip content={`Stress: ${Math.round(stressScore * 100)}%${triggerReasons.length > 0 ? ` | ${triggerReasons.map((r) => REASON_LABELS[r as ToneReasonCode] ?? r).join(', ')}` : ''}`}>
        <div
          className={`${getStressColor()} ${className}`}
        >
          <StressIcon stressScore={stressScore} />
        </div>
      </Tooltip>
    );
  }

  // Full indicator with reasons
  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {/* Stress level indicator */}
      <div
        className={`flex items-center gap-1 px-2 py-1 rounded-full ${getStressBgColor()} ${getStressColor()}`}
      >
        <StressIcon stressScore={stressScore} />
        <span className="text-xs font-medium">{Math.round(stressScore * 100)}%</span>
      </div>

      {/* Trigger reason badges */}
      {triggerReasons.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {triggerReasons.slice(0, 3).map((reason) => (
            <span
              key={reason}
              className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-white/10 text-white/70"
            >
              {REASON_LABELS[reason as ToneReasonCode] ?? reason}
            </span>
          ))}
          {triggerReasons.length > 3 && (
            <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-white/10 text-white/70">
              +{triggerReasons.length - 3}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

export default ToneIndicator;
